import { ChangeDetectionStrategy, Component, inject, model, signal, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbTimeAdapter, NgbTimepicker, NgbTimepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { formatISO, parse } from 'date-fns';
import * as lodash from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { Workshift, WorkshiftResource, WorkshiftUserEventSummary } from '../../modules/admin/workshifts/workshifts.types';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { WorkshiftsService } from '../../modules/admin/workshifts/workshifts.service';
import { TimeConverterComponent } from '../../core/components/timeconverter/timeconverter.component';

@Component({
    selector: 'app-add-user-events-dialog',
    templateUrl: './add-user-event-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
        .edit-user-events-dialog-panel {

            .mat-mdc-dialog-container {
        
                .mat-mdc-dialog-surface {
                    padding: 0 !important;
                }
            }
        }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, MatButtonModule, MatDatepickerModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule, NgbTimepickerModule, ReactiveFormsModule, TimeConverterComponent, TranslocoModule]
})
export class AddUserEventDialogComponent {

    public data: { user: WorkshiftResource, workshift: Workshift } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<AddUserEventDialogComponent>);
    private dialogsService = inject(DialogsService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private matSnackBar = inject(MatSnackBar);
    private ngbTimeAdapter: NgbTimeAdapter<string> = inject(NgbTimeAdapter<string>);
    private workshiftsService = inject(WorkshiftsService);
    private transloco = inject(TranslocoService);
    
    userName = signal(this.data.user.userName);
    checkIn = model<Date>(parse(this.data.workshift.scheduled_from_date, 'yyyy-MM-dd', new Date()));
    checkInTime = model<string>(undefined);
    checkOut = model<Date>(parse(this.data.workshift.scheduled_from_date, 'yyyy-MM-dd', new Date()));
    checkOutTime = model<string>(undefined);
    workTime = new FormControl(0, [Validators.required, Validators.min(0)]);
    pauseTime = new FormControl(0, [Validators.required, Validators.min(0)]);

    constructor() {
    }

    async save() {
        const confirmSave = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('services.dialog.editUserEvents.addConfirm'));
        if (confirmSave) {
            if (this.workTime.invalid || this.pauseTime.invalid || !this.checkInTime() || !this.checkOutTime() || !this.checkIn() || !this.checkOut()) {
                this.dialogsService.openErrorDialog({
                    message: this.transloco.translate('services.dialog.editUserEvents.incompleteDatesError')
                });
                return;
            }
            try {
                const checkInDate = lodash.cloneDeep(this.checkIn());
                if (checkInDate) {
                    checkInDate.setHours(Number(this.checkInTime().substring(0, 2)), Number(this.checkInTime().substring(3, 5)));
                }
                const checkOutDate = lodash.cloneDeep(this.checkOut());
                if (checkOutDate) {
                    checkOutDate.setHours(Number(this.checkOutTime().substring(0, 2)), Number(this.checkOutTime().substring(3, 5)));
                }
                const eventSummary: WorkshiftUserEventSummary = {
                    checkInTimestamp: checkInDate ? formatISO(checkInDate) : undefined,
                    checkOutTimestamp: checkOutDate ? formatISO(checkOutDate) : undefined,
                    workMinutes: this.workTime.value,
                    pauseMinutes: this.pauseTime.value,
                    serviceId: this.data.workshift.serviceId,
                    userId: this.data.user.userId,
                    userName: this.data.user.userName,
                    workplaceCode: this.data.workshift.workplaceCode,
                    workshiftDate: this.data.workshift.scheduled_from_date,
                    workshiftId: this.data.workshift.workshiftId,
                    externalWorkshiftId: this.data.workshift.externalWorkshiftId,
                }
                const reply = await firstValueFrom(this.workshiftsService.beautifyWorkshiftEvent({
                    serviceId: this.data.workshift.serviceId,
                    workplaceCode: this.data.workshift.workplaceCode,
                    workshiftId: this.data.workshift.workshiftId,
                    userId: this.data.user.userId,
                    eventSummary
                }));
                this.matSnackBar.open(this.transloco.translate('services.dialog.editUserEvents.success'), this.transloco.translate('accept'), { duration: 5000 });
                const a = { summary: reply.eventSummary, daySummary: reply.daySummary }
                this.dialogRef.close({ summary: reply.eventSummary, daySummary: reply.daySummary });
            } catch (error) {
                console.error(error);
                this.dialogsService.openErrorDialog();
            }
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    timeChanged(timepicker: NgbTimepicker, eventTime: string) {
        if ((timepicker.model.minute === undefined || isNaN(timepicker.model.minute))
            && (timepicker.model.hour !== undefined && !isNaN(timepicker.model.hour))) {
            timepicker.model.updateMinute(0);
        }
        if (timepicker.model.isValid(timepicker.seconds)) {
            const time = this.ngbTimeAdapter.toModel({
                hour: timepicker.model.hour,
                minute: timepicker.model.minute,
                second: timepicker.model.second
            });
            if (time !== eventTime) {
                timepicker.onChange(time);
            }
        } else {
            if (eventTime) {
                timepicker.onChange(this.ngbTimeAdapter.toModel(null));
            }
        }
    }

    private confirm(title: string, message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title,
            message,
            actions: {
                confirm: {
                    label: this.transloco.translate('accept')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
